import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Table,
} from "reactstrap";
import {
    FaBan,
    FaEdit,
    FaEye,
    FaEyeSlash,
    FaPlus,
    FaSearch,
    FaUnlock,
    FaUserShield,
} from "react-icons/fa";
import Swal from "sweetalert2";
import userService from "../../services/userService";
import posService from "../../services/posService";
import {
    ROLE_ADMIN,
    ROLE_CAISSE,
    ROLE_MANAGER,
    ROLE_SUPER_ADMIN,
} from "../../helpers/constants";

const emptyForm = {
    firstName: "",
    lastName: "",
    username: "",
    phoneNumber: "",
    password: "",
    role: ROLE_CAISSE,
    enabled: true,
    pointOfSaleId: "",
};

const roleLabels = {
    [ROLE_SUPER_ADMIN]: "Super admin",
    [ROLE_ADMIN]: "Admin",
    //[ROLE_MANAGER]: "Manager",
    [ROLE_CAISSE]: "Caisse",
};

const roleOptions = [
    ROLE_CAISSE,
    //ROLE_MANAGER,
    ROLE_ADMIN,
    ROLE_SUPER_ADMIN,
];

const digitsOnly = (value = "") => value.replace(/\D/g, "");

const UserManagementPage = () => {
    const [users, setUsers] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [pointOfSales, setPointOfSales] = useState([]);
    const [showPassword, setShowPassword] = useState(false);

    const isEditing = Boolean(editingUser?.id);

    const activeCount = useMemo(
        () => users.filter((user) => user.enabled !== false).length,
        [users]
    );

    const blockedCount = users.length - activeCount;

    const loadUsers = useCallback(async (search = "") => {
        setLoading(true);
        try {
            const data = await userService.search(search);
            setUsers(Array.isArray(data) ? data : []);
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Chargement impossible",
                text: error?.response?.data?.message || error.message,
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadUsers("");
    }, [loadUsers]);

    useEffect(() => {
        const loadPointOfSales = async () => {
            try {
                const { data } = await posService.getActive();
                setPointOfSales(data?.body || data || []);
            } catch (error) {
                setPointOfSales([]);
            }
        };

        loadPointOfSales();
    }, []);

    const openCreateModal = () => {
        setEditingUser(null);
        setForm(emptyForm);
        setShowPassword(false);
        setModalOpen(true);
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        setForm({
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            username: user.username || "",
            phoneNumber: user.phoneNumber || "",
            password: "",
            role: user.role || ROLE_CAISSE,
            enabled: user.enabled !== false,
            pointOfSaleId: user.pointOfSaleId || "",
        });
        setShowPassword(false);
        setModalOpen(true);
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]:
                type === "checkbox"
                    ? checked
                    : ["username", "password"].includes(name)
                        ? digitsOnly(value)
                        : value,
        }));
    };

    const validateForm = () => {
        if (!form.firstName.trim()) return "Prenom obligatoire";
        if (!form.lastName.trim()) return "Nom obligatoire";
        if (!form.username.trim()) return "Identifiant obligatoire";
        if (!/^\d+$/.test(form.username.trim())) return "Identifiant: chiffres uniquement";
        if (!form.phoneNumber.trim()) return "Telephone obligatoire";
        if (!isEditing && !form.password.trim()) return "Mot de passe obligatoire";
        if (form.password.trim() && !/^\d+$/.test(form.password.trim())) return "Mot de passe: chiffres uniquement";
        return null;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            Swal.fire({ icon: "warning", title: validationError });
            return;
        }

        const payload = {
            ...form,
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            username: form.username.trim(),
            phoneNumber: form.phoneNumber.trim(),
            password: form.password.trim(),
            pointOfSaleId: form.pointOfSaleId ? Number(form.pointOfSaleId) : null,
        };

        if (isEditing && !payload.password) {
            delete payload.password;
        }

        setSaving(true);
        try {
            console.log("Submitting user payload:", payload);
            if (isEditing) {
                await userService.update(editingUser.id, payload);
            } else {
                await userService.create(payload);
            }

            setModalOpen(false);
            await loadUsers(query);
            Swal.fire({
                icon: "success",
                title: "Utilisateur enregistre",
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Enregistrement impossible",
                text: error?.response?.data?.message || error.message,
            });
        } finally {
            setSaving(false);
        }
    };

    const handleToggleEnabled = async (user) => {
        const nextEnabled = user.enabled === false;
        const result = await Swal.fire({
            icon: nextEnabled ? "question" : "warning",
            title: nextEnabled ? "Debloquer cet utilisateur ?" : "Bloquer cet utilisateur ?",
            text: nextEnabled
                ? "Il pourra se connecter a nouveau."
                : "Il ne pourra plus se connecter avec ses identifiants.",
            showCancelButton: true,
            confirmButtonText: nextEnabled ? "Debloquer" : "Bloquer",
            cancelButtonText: "Annuler",
            confirmButtonColor: nextEnabled ? "#198754" : "#dc3545",
        });

        if (!result.isConfirmed) return;

        try {
            await userService.setEnabled(user.id, nextEnabled);
            await loadUsers();
            Swal.fire({
                icon: "success",
                title: nextEnabled ? "Utilisateur debloque" : "Utilisateur bloque",
                text: nextEnabled
                    ? "Le compte peut se connecter a nouveau."
                    : "Le compte ne peut plus se connecter.",
                timer: 1600,
                showConfirmButton: false,
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Action impossible",
                text: error?.response?.data?.message || error.message,
            });
        }
    };

    const handleSearch = (event) => {
        event.preventDefault();
        loadUsers(query);
    };

    return (
        <div>
            <Row className="g-3 mb-3">
                <Col md={4}>
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <div className="text-muted small">Utilisateurs</div>
                                    <h4 className="mb-0">{users.length}</h4>
                                </div>
                                <FaUserShield className="text-primary" size={26} />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <div className="text-muted small">Actifs</div>
                            <h4 className="mb-0 text-success">{activeCount}</h4>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <div className="text-muted small">Bloques</div>
                            <h4 className="mb-0 text-danger">{blockedCount}</h4>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Card className="shadow-sm">
                <CardBody>
                    <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                        <h4 className="mb-0 d-flex align-items-center">
                            <FaUserShield className="text-primary me-2" />
                            Gestion des utilisateurs
                        </h4>
                        <Button color="primary" onClick={openCreateModal}>
                            <FaPlus className="me-1" />
                            Nouvel utilisateur
                        </Button>
                    </div>

                    <Form onSubmit={handleSearch} className="mb-3">
                        <Row className="g-2">
                            <Col md={10}>
                                <div className="position-relative">
                                    <FaSearch
                                        className="position-absolute text-muted"
                                        style={{ left: 12, top: 12 }}
                                    />
                                    <Input
                                        value={query}
                                        onChange={(event) => setQuery(event.target.value)}
                                        placeholder="Rechercher par nom, identifiant ou telephone"
                                        style={{ paddingLeft: 36 }}
                                    />
                                </div>
                            </Col>
                            <Col md={2}>
                                <Button color="secondary" outline className="w-100" disabled={loading}>
                                    Rechercher
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    <div className="table-responsive">
                        <Table hover className="align-middle">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Identifiant</th>
                                    <th>Telephone</th>
                                    <th>Point de vente</th>
                                    <th>Role</th>
                                    <th>Statut</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4 text-muted">
                                            Chargement...
                                        </td>
                                    </tr>
                                ) : users.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4 text-muted">
                                            Aucun utilisateur trouve.
                                        </td>
                                    </tr>
                                ) : (
                                    users.map((user) => (
                                        <tr key={user.id}>
                                            <td>
                                                <div className="fw-semibold">
                                                    {user.firstName} {user.lastName}
                                                </div>
                                                <div className="small text-muted">{user.businessName}</div>
                                            </td>
                                            <td>{user.username}</td>
                                            <td>{user.phoneNumber}</td>
                                            <td>{user.pointOfSaleName || "-"}</td>
                                            <td>{roleLabels[user.role] || user.role}</td>
                                            <td>
                                                <Badge color={user.enabled === false ? "danger" : "success"}>
                                                    {user.enabled === false ? "Bloque" : "Actif"}
                                                </Badge>
                                            </td>
                                            <td className="text-end">
                                                <Button
                                                    color="light"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => openEditModal(user)}
                                                    title="Modifier"
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    color={user.enabled === false ? "success" : "danger"}
                                                    size="sm"
                                                    outline={user.enabled !== false}
                                                    onClick={() => handleToggleEnabled(user)}
                                                    title={user.enabled === false ? "Debloquer" : "Bloquer"}
                                                >
                                                    {user.enabled === false ? <FaUnlock /> : <FaBan />}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
            </Card>

            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
                <ModalHeader toggle={() => setModalOpen(false)}>
                    {isEditing ? "Modifier utilisateur" : "Nouvel utilisateur"}
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Prenom</Label>
                                    <Input name="firstName" value={form.firstName} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Nom</Label>
                                    <Input name="lastName" value={form.lastName} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Identifiant</Label>
                                    <Input
                                        name="username"
                                        value={form.username}
                                        onChange={handleChange}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                    />
                                    <div className="form-text">Chiffres uniquement.</div>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Role</Label>
                                    <Input type="select" name="role" value={form.role} onChange={handleChange}>
                                        {roleOptions.map((role) => (
                                            <option key={role} value={role}>
                                                {roleLabels[role]}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Telephone</Label>
                                    <Input name="phoneNumber" value={form.phoneNumber} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Point de vente</Label>
                                    <Input
                                        type="select"
                                        name="pointOfSaleId"
                                        value={form.pointOfSaleId}
                                        onChange={handleChange}
                                    >
                                        <option value="">Aucun point de vente</option>
                                        {pointOfSales.map((pos) => (
                                            <option key={pos.id} value={pos.id}>
                                                {pos.code ? `${pos.code} - ${pos.name}` : pos.name}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>{isEditing ? "Nouveau mot de passe" : "Mot de passe"}</Label>
                                    <div className="position-relative">
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={form.password}
                                            onChange={handleChange}
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            placeholder={isEditing ? "Laisser vide pour conserver" : ""}
                                        />
                                        <Button
                                            type="button"
                                            color="link"
                                            className="position-absolute end-0 top-50 translate-middle-y border-0"
                                            onClick={() => setShowPassword((value) => !value)}
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </Button>
                                    </div>
                                    <div className="form-text">Chiffres uniquement.</div>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup check className="mt-4 pt-2">
                                    <Input
                                        type="checkbox"
                                        name="enabled"
                                        checked={form.enabled}
                                        onChange={handleChange}
                                    />
                                    <Label check>Compte actif</Label>
                                </FormGroup>
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button type="submit" color="primary" disabled={saving}>
                            {saving ? "Enregistrement..." : "Enregistrer"}
                        </Button>
                        <Button type="button" color="secondary" onClick={() => setModalOpen(false)}>
                            Annuler
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </div>
    );
};

export default UserManagementPage;
